import { ApiError, type SocialDisplay, type SocialPlatform, type SocialPost } from "@brandex/api-client";
import { formatDate, formatRelative, type Locale, type MessageKey } from "@brandex/i18n";
import { Alert, Avatar, Badge, Button, EmptyState, Field, Input, Select, Spinner, cn } from "@brandex/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  EyeOff,
  Eye,
  ExternalLink,
  Heart,
  ImageOff,
  LogIn,
  Star,
  MessageCircle,
  Pin,
  PinOff,
  RefreshCw,
  Repeat2,
  Search,
  Unplug,
  Users,
} from "lucide-react";
import { useState } from "react";

import { useBrandex, useT } from "../../context";
import { errorField, errorMessage } from "../../errors";
import { keys, mediaUrl, useSocialPosts, type SocialPostFilter } from "../../queries";
import { ConnectGuide } from "./guide";
import { GUIDES, type SocialKey } from "./guides";

/** Compact numbers: 12.4K, ১২.৪ হা. */
export function compact(locale: Locale, n: number): string {
  return new Intl.NumberFormat(locale === "bn" ? "bn-BD" : "en", { notation: "compact", maximumFractionDigits: 1 }).format(n);
}

function duration(secs: number): string {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = String(secs % 60).padStart(2, "0");
  return h > 0 ? `${String(h)}:${String(m).padStart(2, "0")}:${s}` : `${String(m)}:${s}`;
}

export const STATUS_TONE = { ok: "success", pending: "accent", needs_reconnect: "warning", failing: "warning" } as const;

/** Whether a platform's posts are on the website right now. */
export function onSite(p: SocialPlatform): boolean {
  return p.connected && p.display.show_on_site && p.posts > 0;
}

/** One platform's tab: connection, website settings, pinned and all posts. */
export function PlatformPanel({ orgId, platform, panel, staff }: { orgId: string; platform: SocialKey; panel: SocialPlatform; staff: boolean }) {
  const t = useT();
  const name = t(`social.platform.${platform}` as MessageKey);
  const healthy = panel.connected && (panel.sync?.status === "ok" || panel.sync?.status === "pending");
  return (
    <div className="grid gap-6">
      {!panel.connected ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:items-start">
          <NotConnected orgId={orgId} platform={platform} panel={panel} staff={staff} />
          <ConnectGuide platform={platform} panel={panel} staff={staff} open />
        </div>
      ) : (
        <>
          <AccountCard orgId={orgId} platform={platform} panel={panel} staff={staff} />
          {!healthy ? <ConnectGuide platform={platform} panel={panel} staff={staff} open /> : null}
          <DisplaySettings orgId={orgId} platform={platform} panel={panel} />
          <Pinned orgId={orgId} panel={panel} name={name} />
          <Posts orgId={orgId} panel={panel} name={name} />
          {healthy ? <ConnectGuide platform={platform} panel={panel} staff={staff} open={false} /> : null}
        </>
      )}
    </div>
  );
}

function NotConnected({ orgId, platform, panel, staff }: { orgId: string; platform: SocialKey; panel: SocialPlatform; staff: boolean }) {
  const t = useT();
  const name = t(`social.platform.${platform}` as MessageKey);
  if (!panel.configured) {
    return (
      <div className="grid gap-3 rounded-[var(--radius-lg)] border border-border bg-card p-5">
        <h2 className="font-display text-lg font-semibold">{t("social.connect.title", { platform: name })}</h2>
        <Alert tone="info">{t(staff ? "social.notConfigured.staff" : "social.notConfigured.doctor", { platform: name })}</Alert>
        {!GUIDES[platform].byLink ? <p className="text-sm text-muted-foreground">{t("social.connect.prepare", { platform: name })}</p> : null}
      </div>
    );
  }
  if (!GUIDES[platform].byLink) return <ConnectBySignIn orgId={orgId} platform={platform} name={name} staff={staff} />;
  return <ConnectByLink orgId={orgId} platform={platform} name={name} />;
}

/**
 * Opens the provider's sign-in page (ADR 0025). The browser comes back to
 * this tab with the accounts to choose from. The desktop app opens the
 * system browser; the choice is then made in the web portal.
 */
export function ConnectBySignIn({
  orgId,
  platform,
  name,
  staff,
  compact = false,
}: {
  orgId: string;
  platform: SocialKey;
  name: string;
  staff: boolean;
  compact?: boolean;
}) {
  const t = useT();
  const { api, platform: host } = useBrandex();
  const [opened, setOpened] = useState(false);
  const start = useMutation({
    mutationFn: async () => {
      const res = await api.http.POST("/v1/orgs/{org_id}/social/{platform}/oauth", {
        params: { path: { org_id: orgId, platform } },
        body: { return_to: staff ? "admin" : "portal" },
      });
      if (res.data === undefined) throw new Error("Empty response body");
      return res.data.url;
    },
    onSuccess: async (url) => {
      if (host.kind === "desktop") {
        await host.openExternal(url);
        setOpened(true);
      } else {
        window.location.assign(url);
      }
    },
  });
  const code = start.error instanceof ApiError ? start.error.code : null;
  const button = (
    <Button onClick={() => start.mutate()} loading={start.isPending} variant={compact ? "outline" : "primary"} size={compact ? "sm" : "md"} className={compact ? "" : "w-full sm:w-fit"}>
      <LogIn aria-hidden className="h-4 w-4" />
      {t(compact ? "social.connect.reconnect" : "social.connect.signIn", { platform: name })}
    </Button>
  );
  const errors = start.isError ? (
    <Alert tone="error">
      {code === "social_not_configured"
        ? t(staff ? "social.notConfigured.staff" : "social.notConfigured.doctor", { platform: name })
        : code === "social_callback_missing"
          ? t(staff ? "social.callbackMissing.staff" : "social.notConfigured.doctor", { platform: name })
          : errorMessage(start.error, t)}
    </Alert>
  ) : null;
  if (compact) {
    return (
      <>
        {button}
        {errors}
      </>
    );
  }
  return (
    <div className="grid gap-4 rounded-[var(--radius-lg)] border border-border bg-card p-5">
      <div>
        <h2 className="font-display text-lg font-semibold">{t("social.connect.title", { platform: name })}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t(`social.connect.${platform}.lead` as MessageKey)}</p>
      </div>
      {staff ? <p className="text-sm text-muted-foreground">{t("social.connect.staffSignIn", { platform: name })}</p> : null}
      {button}
      {opened ? <Alert tone="info">{t("social.connect.inBrowser", { platform: name })}</Alert> : null}
      {errors}
    </div>
  );
}

function ConnectByLink({ orgId, platform, name }: { orgId: string; platform: SocialKey; name: string }) {
  const t = useT();
  const { api } = useBrandex();
  const qc = useQueryClient();
  const [link, setLink] = useState("");
  const connect = useMutation({
    mutationFn: async () => {
      await api.http.PUT("/v1/orgs/{org_id}/social/{platform}/link", {
        params: { path: { org_id: orgId, platform } },
        body: { link: link.trim() },
      });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.social(orgId) }),
  });
  const notFound = errorField(connect.error) === "account";
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (link.trim() !== "") connect.mutate();
      }} className="grid gap-4 rounded-[var(--radius-lg)] border border-border bg-card p-5" noValidate>
      <div>
        <h2 className="font-display text-lg font-semibold">{t("social.connect.title", { platform: name })}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t(`social.connect.${platform}.lead` as MessageKey)}</p>
      </div>
      <Field
        label={t(`social.connect.${platform}.label` as MessageKey)}
        hint={t(`social.connect.${platform}.hint` as MessageKey)}
        error={notFound ? t(`social.connect.${platform}.notFound` as MessageKey) : undefined}
      >
        <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder={t(`social.connect.${platform}.placeholder` as MessageKey)} inputMode="url" autoComplete="off" spellCheck={false} />
      </Field>
      {connect.isError && !notFound ? (
        <Alert tone="error">
          {connect.error instanceof ApiError && connect.error.code === "social_not_configured" ? t("social.notConfigured.doctor", { platform: name }) : errorMessage(connect.error, t)}
        </Alert>
      ) : null}
      <Button type="submit" loading={connect.isPending} disabled={link.trim() === ""} className="w-full sm:w-fit">
        {t("social.connect.button", { platform: name })}
      </Button>
    </form>
  );
}

function AccountCard({ orgId, platform, panel, staff }: { orgId: string; platform: SocialKey; panel: SocialPlatform; staff: boolean }) {
  const t = useT();
  const { locale, api } = useBrandex();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const sync = panel.sync;
  const profile = sync?.profile;
  const stats = sync?.stats;
  const refresh = () => void qc.invalidateQueries({ queryKey: keys.social(orgId) });
  const syncNow = useMutation({
    mutationFn: async () => {
      await api.http.POST("/v1/orgs/{org_id}/social/{platform}/sync", { params: { path: { org_id: orgId, platform } } });
    },
    onSuccess: refresh,
  });
  const disconnect = useMutation({
    mutationFn: async () => {
      await api.http.DELETE("/v1/orgs/{org_id}/social/{platform}", { params: { path: { org_id: orgId, platform } } });
    },
    onSuccess: refresh,
  });
  const status = sync?.status ?? "pending";
  const reviews = platform === "google";
  const numbers: { key: MessageKey; value: string | null }[] = reviews
    ? [
        { key: "social.stats.rating", value: stats?.rating === null || stats?.rating === undefined ? null : `${stars(locale, stats.rating)} ★` },
        { key: "social.stats.reviews", value: stats?.posts === null || stats?.posts === undefined ? null : compact(locale, stats.posts) },
        { key: "social.stats.synced", value: compact(locale, panel.posts) },
      ]
    : [
        { key: platform === "youtube" ? "social.stats.subscribers" : "social.stats.followers", value: stats?.followers === null || stats?.followers === undefined ? null : compact(locale, stats.followers) },
        { key: "social.stats.views", value: stats?.total_views === null || stats?.total_views === undefined ? null : compact(locale, stats.total_views) },
        { key: "social.stats.posts", value: compact(locale, panel.posts) },
      ];
  const name = t(`social.platform.${platform}` as MessageKey);
  const reconnect = panel.connect === "oauth" && (status === "needs_reconnect" || status === "failing");
  return (
    <section aria-label={t("social.account.title")} className="grid gap-4 rounded-[var(--radius-lg)] border border-border bg-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          {profile?.avatar_url ? (
            <img src={profile.avatar_url} alt="" className="h-12 w-12 shrink-0 rounded-full object-cover" referrerPolicy="no-referrer" />
          ) : (
            <Avatar name={profile?.name ?? ""} />
          )}
          <div className="min-w-0">
            <p className="truncate font-semibold">{profile?.name ?? t(`social.platform.${platform}` as MessageKey)}</p>
            {profile?.url ? (
              <a href={profile.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline">
                {profile.handle ?? profile.url}
                <ExternalLink aria-hidden className="h-3 w-3" />
              </a>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={STATUS_TONE[status]}>{t(`social.status.${status}` as MessageKey)}</Badge>
          {reconnect ? <ConnectBySignIn orgId={orgId} platform={platform} name={name} staff={staff} compact /> : null}
          <Button variant="outline" size="sm" onClick={() => syncNow.mutate()} loading={syncNow.isPending}>
            <RefreshCw aria-hidden className="h-4 w-4" />
            {t("social.account.syncNow")}
          </Button>
          {confirm ? (
            <span className="inline-flex items-center gap-2 rounded-md border border-destructive/40 px-2 py-1 text-sm">
              {t("social.account.disconnectConfirm")}
              <Button variant="destructive" size="sm" onClick={() => disconnect.mutate()} loading={disconnect.isPending}>
                {t("social.account.disconnectYes")}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirm(false)}>
                {t("common.cancel")}
              </Button>
            </span>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setConfirm(true)}>
              <Unplug aria-hidden className="h-4 w-4" />
              {t("social.account.disconnect")}
            </Button>
          )}
        </div>
      </div>
      <dl className="grid grid-cols-3 gap-3">
        {numbers.map((n) => (
          <div key={n.key} className="rounded-md bg-muted/60 px-3 py-2">
            <dt className="text-xs text-muted-foreground">{t(n.key)}</dt>
            <dd className="font-display text-xl font-bold">{n.value ?? "—"}</dd>
          </div>
        ))}
      </dl>
      <p className="text-xs text-muted-foreground">
        {status === "pending"
          ? t("social.account.loading")
          : sync?.last_synced_at
            ? t("social.account.synced", { when: formatRelative(locale, sync.last_synced_at), source: stats?.source ?? "" })
            : null}
      </p>
      {sync?.problem ? (
        <Alert tone="error">{t(`social.problem.${sync.problem.code}.short` as MessageKey, { platform: t(`social.platform.${platform}` as MessageKey) })}</Alert>
      ) : null}
      {syncNow.isError ? <Alert tone="error">{errorMessage(syncNow.error, t)}</Alert> : null}
      {disconnect.isError ? <Alert tone="error">{errorMessage(disconnect.error, t)}</Alert> : null}
    </section>
  );
}

function DisplaySettings({ orgId, platform, panel }: { orgId: string; platform: SocialKey; panel: SocialPlatform }) {
  const t = useT();
  const { api } = useBrandex();
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: async (display: SocialDisplay) => {
      await api.http.PUT("/v1/orgs/{org_id}/social/{platform}/display", { params: { path: { org_id: orgId, platform } }, body: display });
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.social(orgId) }),
  });
  // Show the choice at once; the server's answer replaces it.
  const d = save.isPending ? save.variables : panel.display;
  const change = (patch: Partial<SocialDisplay>) => save.mutate({ ...d, ...patch });
  return (
    <section aria-labelledby={`site-${platform}`} className="grid gap-4 rounded-[var(--radius-lg)] border border-border bg-card p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id={`site-${platform}`} className="font-display text-lg font-semibold">{t("social.site.title")}</h2>
        <Badge tone={onSite(panel) ? "success" : "neutral"}>{t(onSite(panel) ? "social.site.on" : "social.site.off")}</Badge>
      </div>
      <label className="flex items-start gap-3 text-sm">
        <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[var(--color-primary)]" checked={d.show_on_site} onChange={(e) => change({ show_on_site: e.target.checked })} />
        <span>
          <span className="font-medium">{t("social.site.show")}</span>
          <span className="block text-muted-foreground">{t("social.site.showHint")}</span>
        </span>
      </label>
      <div className={cn("grid gap-4 sm:grid-cols-2", !d.show_on_site && "opacity-60")}>
        <Field label={t("social.site.count")}>
          <Select value={String(d.count)} disabled={!d.show_on_site} onChange={(e) => change({ count: Number(e.target.value) })}>
            {[3, 6, 9].map((n) => (
              <option key={n} value={n}>
                {t("social.site.countN", { n })}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("social.site.fill")} hint={t("social.site.fillHint")}>
          <Select value={d.fill} disabled={!d.show_on_site} onChange={(e) => change({ fill: e.target.value as SocialDisplay["fill"] })}>
            {(platform === "google" ? (["latest", "highest_rated"] as const) : (["latest", "most_viewed", "most_reactions"] as const)).map((f) => (
              <option key={f} value={f}>
                {t(`social.fill.${f}` as MessageKey)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <label className={cn("flex items-start gap-3 text-sm", !d.show_on_site && "opacity-60")}>
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 accent-[var(--color-primary)]"
          checked={d.show_followers}
          disabled={!d.show_on_site}
          onChange={(e) => change({ show_followers: e.target.checked })}
        />
        <span>
          <span className="font-medium">{t(platform === "youtube" ? "social.site.subscribers" : platform === "google" ? "social.site.rating" : "social.site.followers")}</span>
          <span className="block text-muted-foreground">{t("social.site.followersHint")}</span>
        </span>
      </label>
      {save.isError ? <Alert tone="error">{errorMessage(save.error, t)}</Alert> : null}
    </section>
  );
}

function usePostActions(orgId: string) {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const done = () => void qc.invalidateQueries({ queryKey: keys.social(orgId) });
  const pin = useMutation({
    mutationFn: async ({ id, pinned }: { id: string; pinned: boolean }) => {
      await api.http.PUT("/v1/orgs/{org_id}/social/posts/{post_id}/pin", { params: { path: { org_id: orgId, post_id: id } }, body: { pinned } });
    },
    onSettled: done,
  });
  const hide = useMutation({
    mutationFn: async ({ id, hidden }: { id: string; hidden: boolean }) => {
      await api.http.PUT("/v1/orgs/{org_id}/social/posts/{post_id}/hidden", { params: { path: { org_id: orgId, post_id: id } }, body: { hidden } });
    },
    onSettled: done,
  });
  return { pin, hide };
}

function Pinned({ orgId, panel, name }: { orgId: string; panel: SocialPlatform; name: string }) {
  const t = useT();
  const { api } = useBrandex();
  const qc = useQueryClient();
  const filter: SocialPostFilter = { sort: "newest", show: "pinned", q: "" };
  const posts = useSocialPosts(orgId, panel.platform, filter, false);
  const { pin } = usePostActions(orgId);
  const order = useMutation({
    mutationFn: async (ids: string[]) => {
      await api.http.PUT("/v1/orgs/{org_id}/social/{platform}/pins", { params: { path: { org_id: orgId, platform: panel.platform } }, body: { post_ids: ids } });
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.social(orgId) }),
  });
  const list = posts.data?.pages.flatMap((p) => p.items) ?? [];
  const move = (from: number, to: number) => {
    const ids = list.map((p) => p.id);
    const [moved] = ids.splice(from, 1);
    if (moved === undefined) return;
    ids.splice(to, 0, moved);
    order.mutate(ids);
  };
  return (
    <section aria-labelledby={`pinned-${panel.platform}`} className="grid gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id={`pinned-${panel.platform}`} className="font-display text-lg font-semibold">
          {t("social.pinned.title", { pinned: panel.pinned, max: panel.max_pinned })}
        </h2>
        <p className="text-sm text-muted-foreground">{t("social.pinned.hint", { platform: name })}</p>
      </div>
      {posts.isPending ? <Spinner label={t("common.loading")} /> : null}
      {posts.isSuccess && list.length === 0 ? <p className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">{t("social.pinned.empty")}</p> : null}
      {list.length > 0 ? (
        <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((p, i) => (
            <li key={p.id} className="flex gap-3 rounded-md border border-border bg-card p-2">
              <Thumb post={p} className="w-28 shrink-0" />
              <div className="grid min-w-0 flex-1 content-between gap-1">
                <p className="line-clamp-2 text-sm font-medium">{p.caption || t("social.post.untitled")}</p>
                <div className="flex items-center gap-1">
                  <Badge tone="accent">{i + 1}</Badge>
                  <Button variant="ghost" size="icon" aria-label={t("social.pinned.earlier", { title: p.caption })} disabled={i === 0 || order.isPending} onClick={() => move(i, i - 1)}>
                    <ArrowLeft aria-hidden className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" aria-label={t("social.pinned.later", { title: p.caption })} disabled={i === list.length - 1 || order.isPending} onClick={() => move(i, i + 1)}>
                    <ArrowRight aria-hidden className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" aria-label={t("social.post.unpinLabel", { title: p.caption })} onClick={() => pin.mutate({ id: p.id, pinned: false })}>
                    <PinOff aria-hidden className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ol>
      ) : null}
      {order.isError ? <Alert tone="error">{errorMessage(order.error, t)}</Alert> : null}
      {pin.isError ? <Alert tone="error">{errorMessage(pin.error, t)}</Alert> : null}
    </section>
  );
}

function Posts({ orgId, panel, name }: { orgId: string; panel: SocialPlatform; name: string }) {
  const t = useT();
  const [filter, setFilter] = useState<SocialPostFilter>({ sort: "newest", show: "all", q: "" });
  const [search, setSearch] = useState("");
  const loading = panel.sync?.status === "pending";
  const posts = useSocialPosts(orgId, panel.platform, filter, loading);
  const { pin, hide } = usePostActions(orgId);
  const list = posts.data?.pages.flatMap((p) => p.items) ?? [];
  const total = posts.data?.pages[0]?.total ?? 0;
  const full = panel.pinned >= panel.max_pinned;
  return (
    <section aria-labelledby={`posts-${panel.platform}`} className="grid gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 id={`posts-${panel.platform}`} className="font-display text-lg font-semibold">
          {t("social.posts.title", { platform: name })} <span className="text-sm font-normal text-muted-foreground">({total})</span>
        </h2>
        <div className="flex w-full flex-wrap gap-2 sm:w-auto">
          <div role="group" aria-label={t("social.posts.show")} className="inline-flex rounded-md border border-border p-0.5">
            {(["all", "pinned", "hidden"] as const).map((s) => (
              <button
                key={s}
                type="button"
                aria-pressed={filter.show === s}
                onClick={() => setFilter({ ...filter, show: s })}
                className={cn("rounded px-3 py-1.5 text-sm", filter.show === s ? "bg-primary text-primary-foreground" : "hover:bg-muted")}
              >
                {t(`social.show.${s}` as MessageKey)}
              </button>
            ))}
          </div>
          <Select aria-label={t("social.posts.sort")} value={filter.sort} onChange={(e) => setFilter({ ...filter, sort: e.target.value as SocialPostFilter["sort"] })} className="w-auto">
            {(panel.platform === "google" ? (["newest", "highest_rated"] as const) : (["newest", "most_viewed", "most_reactions", "most_reach"] as const)).map((s) => (
              <option key={s} value={s}>
                {t(`social.sort.${s}` as MessageKey)}
              </option>
            ))}
          </Select>
          <form
            role="search"
            className="relative min-w-48 flex-1 sm:flex-none"
            onSubmit={(e) => {
              e.preventDefault();
              setFilter({ ...filter, q: search });
            }}
          >
            <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              aria-label={t("social.posts.search")}
              placeholder={t("social.posts.search")}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (e.target.value === "") setFilter({ ...filter, q: "" });
              }}
              className="pl-9"
            />
          </form>
        </div>
      </div>
      {posts.isPending ? <Spinner label={t("common.loading")} /> : null}
      {posts.isError ? <Alert tone="error">{errorMessage(posts.error, t)}</Alert> : null}
      {posts.isSuccess && list.length === 0 ? (
        <EmptyState
          icon={<ImageOff aria-hidden className="h-8 w-8 text-muted-foreground" />}
          title={t(loading ? "social.posts.loadingTitle" : "social.posts.emptyTitle")}
          description={t(loading ? "social.account.loading" : filter.show === "all" && filter.q === "" ? "social.posts.empty" : "social.posts.noMatch", { platform: name })}
        />
      ) : null}
      {list.length > 0 ? (
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {list.map((p) => (
            <PostCard key={p.id} post={p} full={full} busy={pin.isPending || hide.isPending} onPin={(pinned) => pin.mutate({ id: p.id, pinned })} onHide={(hidden) => hide.mutate({ id: p.id, hidden })} />
          ))}
        </ul>
      ) : null}
      {pin.isError ? <Alert tone="error">{errorMessage(pin.error, t)}</Alert> : null}
      {hide.isError ? <Alert tone="error">{errorMessage(hide.error, t)}</Alert> : null}
      {posts.hasNextPage ? (
        <Button variant="outline" className="justify-self-center" onClick={() => void posts.fetchNextPage()} loading={posts.isFetchingNextPage}>
          {t("social.posts.more")}
        </Button>
      ) : null}
    </section>
  );
}

/** A star row, read out as "4 of 5 stars". */
export function StarRow({ rating, className }: { rating: number; className?: string }) {
  const t = useT();
  return (
    <span role="img" aria-label={t("social.review.stars", { n: rating })} className={cn("inline-flex gap-0.5 text-amber-500", className)}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} aria-hidden className={cn("h-4 w-4", i <= rating ? "fill-current" : "text-muted-foreground/40")} />
      ))}
    </span>
  );
}

/** A review's reviewer, stars and date. */
function ReviewHead({ post }: { post: SocialPost }) {
  const photo = useImageUrl(post.author?.photo_url);
  const name = post.author?.name ?? "";
  return (
    <div className="flex items-center gap-3">
      {photo !== null ? (
        <img src={photo} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" referrerPolicy="no-referrer" loading="lazy" />
      ) : (
        <Avatar name={name} />
      )}
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold">{name}</p>
        {post.metrics.rating !== null && post.metrics.rating !== undefined ? <StarRow rating={post.metrics.rating} /> : null}
      </div>
    </div>
  );
}

/** Stars with one decimal: 4.7, ৪.৭. */
export function stars(locale: Locale, n: number): string {
  return new Intl.NumberFormat(locale === "bn" ? "bn-BD" : "en", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n);
}

/** Kept copies of post images live on the API; provider links are absolute. */
function useImageUrl(url: string | null | undefined): string | null {
  const { apiBaseUrl } = useBrandex();
  if (url === null || url === undefined) return null;
  return url.startsWith("/") ? mediaUrl(apiBaseUrl, url) : url;
}

function Thumb({ post, className }: { post: SocialPost; className?: string }) {
  const src = useImageUrl(post.thumbnail_url);
  if (post.kind === "review") {
    return (
      <div className={cn("grid aspect-video place-items-center rounded-md bg-amber-500/10", className)}>
        {post.metrics.rating !== null && post.metrics.rating !== undefined ? <StarRow rating={post.metrics.rating} /> : null}
      </div>
    );
  }
  return (
    <div className={cn("relative aspect-video overflow-hidden rounded-md bg-muted", className)}>
      {src !== null ? (
        <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer" className="h-full w-full object-cover" />
      ) : (
        <ImageOff aria-hidden className="absolute inset-0 m-auto h-6 w-6 text-muted-foreground" />
      )}
      {post.duration_secs !== null && post.duration_secs !== undefined ? (
        <span className="absolute bottom-1 right-1 rounded bg-black/75 px-1.5 py-0.5 text-[11px] font-semibold text-white">{duration(post.duration_secs)}</span>
      ) : null}
    </div>
  );
}

function PostCard({ post, full, busy, onPin, onHide }: { post: SocialPost; full: boolean; busy: boolean; onPin: (pinned: boolean) => void; onHide: (hidden: boolean) => void }) {
  const t = useT();
  const { locale } = useBrandex();
  const m = post.metrics;
  const pinned = post.pin_rank !== null && post.pin_rank !== undefined;
  const removed = post.removed_at !== null && post.removed_at !== undefined;
  const review = post.kind === "review";
  const title = review ? t("social.review.by", { name: post.author?.name ?? "" }) : post.caption || t("social.post.untitled");
  const numbers = [
    { icon: Eye, key: "social.metric.views", v: m.views },
    { icon: Users, key: "social.metric.reach", v: m.reach },
    { icon: Heart, key: "social.metric.reactions", v: m.reactions },
    { icon: MessageCircle, key: "social.metric.comments", v: m.comments },
    { icon: Repeat2, key: "social.metric.shares", v: m.shares },
  ].filter((n): n is { icon: typeof Eye; key: string; v: number } => n.v !== null && n.v !== undefined);
  const pinBlocked = !pinned && (full || post.hidden || removed);
  return (
    <li className={cn("grid content-start gap-3 rounded-[var(--radius-lg)] border bg-card p-3", pinned ? "border-primary/60" : "border-border", (post.hidden || removed) && "opacity-70")}>
      {review ? (
        <ReviewHead post={post} />
      ) : (
        <a href={post.permalink} target="_blank" rel="noopener noreferrer" aria-label={t("social.post.open", { title })} className="block">
          <Thumb post={post} />
        </a>
      )}
      <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        <Badge>{t(`social.kind.${post.kind}` as MessageKey)}</Badge>
        {pinned ? <Badge tone="accent">{t("social.post.pinnedN", { n: (post.pin_rank ?? 0) + 1 })}</Badge> : null}
        {post.hidden ? <Badge tone="warning">{t("social.post.hidden")}</Badge> : null}
        {removed ? <Badge tone="warning">{t("social.post.removed")}</Badge> : null}
        <time dateTime={post.published_at}>{formatDate(locale, post.published_at)}</time>
      </div>
      {review ? (
        <>
          <p className={cn("line-clamp-5 text-sm", post.caption === "" && "italic text-muted-foreground")}>{post.caption || t("social.review.noText")}</p>
          {post.reply ? (
            <p className="rounded-md bg-muted/60 px-3 py-2 text-xs">
              <span className="font-semibold">{t("social.review.reply")} </span>
              {post.reply}
            </p>
          ) : null}
        </>
      ) : (
        <p className="line-clamp-2 text-sm font-medium">{title}</p>
      )}
      {numbers.length > 0 ? (
        <ul className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground" aria-label={t("social.metric.label")}>
          {numbers.map(({ icon: Icon, key, v }) => (
            <li key={key} className="inline-flex items-center gap-1" title={t(key as MessageKey)}>
              <Icon aria-hidden className="h-3.5 w-3.5" />
              <span className="sr-only">{t(key as MessageKey)}: </span>
              {compact(locale, v)}
            </li>
          ))}
        </ul>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant={pinned ? "accent" : "outline"}
          disabled={busy || pinBlocked}
          title={pinBlocked ? t(full ? "social.post.pinsFull" : "social.post.cantPin") : undefined}
          aria-label={t(pinned ? "social.post.unpinLabel" : "social.post.pinLabel", { title })}
          onClick={() => onPin(!pinned)}
        >
          {pinned ? <PinOff aria-hidden className="h-4 w-4" /> : <Pin aria-hidden className="h-4 w-4" />}
          {t(pinned ? "social.post.unpin" : "social.post.pin")}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} aria-label={t(post.hidden ? "social.post.showLabel" : "social.post.hideLabel", { title })} onClick={() => onHide(!post.hidden)}>
          {post.hidden ? <Eye aria-hidden className="h-4 w-4" /> : <EyeOff aria-hidden className="h-4 w-4" />}
          {t(post.hidden ? "social.post.show" : "social.post.hide")}
        </Button>
      </div>
    </li>
  );
}
